import {
	AccessoryMetadata,
	BullsharkAttributes,
	BullsharkExpression,
	CapyAttributes,
	CapyExpression,
	SuiFrenAttributes,
	accessories,
	colors,
	earGeneToEarShape,
} from '@suigar/suifrens-display-system';

const EXPRESSIONS: CapyAttributes['expression'][] = [
	'happy',
	'bigSmile',
	'laughing',
	'heartEyes',
	'wink',
	'cool',
	'tongueOut',
	'blush',
	'kiss',
	'relaxed',
	'mischievous',
];
const SKINS: CapyAttributes['skin'][] = ['basic', 'cheetah', 'fox', 'panda', 'stripes', 'snake', 'lizard', 'dalmation'];
const BULLSHARK_EXPRESSIONS: BullsharkExpression[] = [
	'happy',
	'bigSmile',
	'laughing',
	'heartEyes',
	'wink',
	'cool',
	'tongueOut',
	'blush',
	'kiss',
	'relaxed',
	'mischievous',
	'ourah',
	'showing',
];
const BULLSHARK_SKINS: BullsharkAttributes['skin'][] = ['basic', 'cheetah', 'fox', 'panda', 'stripes', 'snake', 'lizard', 'dalmation'];
const FIN_STYLES: BullsharkAttributes['finStyle'][] = ['classic'];

// Simple djb2 hash function
function hashString(str: string): number {
	let hash = 5381;
	for (const char of str) {
		hash = (hash * 33) ^ char.charCodeAt(0);
	}
	return Math.abs(hash);
}

function pickFrom<T>(items: readonly T[], seed: number, shift = 0): T {
	const index = (seed >> shift) % items.length;
	return items[index];
}

function isAccessoryType(type: string): type is AccessoryType {
	return (ACCESSORY_TYPES as readonly string[]).includes(type);
}

// Dark colors to exclude from Suifrens generation (near black, dark gray, very dark colors)
const COLOR_BLACKLIST = new Set([
	'B4B4B4', // Nobel - medium gray
	'707070', // Panda - dark gray
	'4E494E', // Chocolate - very dark gray/brown
	'5F455D', // Steel - dark purple-gray
	'653030', // Nutella - dark brown
	'43426F', // Midnight - very dark blue
	'066011', // Forest - very dark green
]);

const COLOR_KEYS = Object.keys(colors).filter((colorKey) => !COLOR_BLACKLIST.has(colorKey)) as CapyAttributes['mainColor'][];
const EAR_SHAPES = Object.values(earGeneToEarShape) as CapyAttributes['earShape'][];
const ACCESSORY_TYPES = ['head', 'eyes', 'body', 'legs', 'feet', 'back', 'object', 'torso'] as const;
type AccessoryType = (typeof ACCESSORY_TYPES)[number];

const ACCESSORIES_BY_TYPE = accessories.reduce<Record<AccessoryType, AccessoryMetadata[]>>(
	(acc, accessory) => {
		if (!isAccessoryType(accessory.type)) {
			return acc;
		}
		if (!acc[accessory.type]) {
			acc[accessory.type] = [];
		}
		acc[accessory.type]!.push(accessory);
		return acc;
	},
	{} as Record<AccessoryType, AccessoryMetadata[]>,
);

function selectAccessories(seed: number): AccessoryMetadata[] {
	const selectedAccessories = ACCESSORY_TYPES.reduce<AccessoryMetadata[]>((acc, type, idx) => {
		const chance = (seed >> (idx + 1)) % 100;
		if (chance <= 80) {
			return acc;
		}

		const pool = ACCESSORIES_BY_TYPE[type];
		if (!pool || pool.length === 0) {
			return acc;
		}

		acc.push(pool[seed % pool.length]);
		return acc;
	}, []);

	return selectedAccessories;
}

const colorPool = COLOR_KEYS.length > 0 ? COLOR_KEYS : (Object.keys(colors) as CapyAttributes['mainColor'][]);
const earShapePool = EAR_SHAPES.length > 0 ? EAR_SHAPES : (Object.values(earGeneToEarShape) as CapyAttributes['earShape'][]);

function buildCapyAttributes(seed: number): CapyAttributes {
	return {
		mainColor: pickFrom(colorPool, seed),
		secondaryColor: pickFrom(colorPool, seed, 1),
		earShape: pickFrom(earShapePool, seed, 2),
		skin: pickFrom(SKINS, seed, 3),
		expression: pickFrom(EXPRESSIONS, seed, 4),
	};
}

function buildBullsharkAttributes(seed: number): BullsharkAttributes {
	return {
		mainColor: pickFrom(colorPool as BullsharkAttributes['mainColor'][], seed),
		secondaryColor: pickFrom(colorPool as BullsharkAttributes['secondaryColor'][], seed, 1),
		skin: pickFrom(BULLSHARK_SKINS, seed, 2),
		expression: pickFrom(BULLSHARK_EXPRESSIONS, seed, 3),
		finStyle: pickFrom(FIN_STYLES, seed, 4),
	};
}

export function getDeterministicAppearance(playerName: string): {
	attributes: CapyAttributes;
	accessories: AccessoryMetadata[];
	mirror: boolean;
	swayValue: string;
} {
	const seed = hashString(playerName);
	const attributes = buildCapyAttributes(seed);
	const selectedAccessories = selectAccessories(seed);
	const mirror = seed % 2 === 0;
	const swayValue = (2 + (seed % 6)).toFixed(2);
	return { attributes, accessories: selectedAccessories, mirror, swayValue };
}

export type DeterministicSuiFrenResult = {
	variant: 'capy' | 'bullshark';
	attributes: SuiFrenAttributes;
	accessories: AccessoryMetadata[];
	mirror: boolean;
	swayValue: string;
};

type DeterministicOptions = {
	variant?: 'auto' | 'capy' | 'bullshark';
};

export function getDeterministicSuiFren(seedInput: string, options: DeterministicOptions = {}): DeterministicSuiFrenResult {
	const normalizedSeed = seedInput?.trim();
	const fallbackSeed = normalizedSeed && normalizedSeed.length > 0 ? normalizedSeed : 'suigar';
	const seed = hashString(fallbackSeed);
	const variant = options.variant && options.variant !== 'auto' ? options.variant : (seed & 1) === 0 ? ('capy' as const) : ('bullshark' as const);

	const attributes = variant === 'capy' ? buildCapyAttributes(seed) : buildBullsharkAttributes(seed);
	const accessories = selectAccessories(seed);
	const mirror = seed % 2 === 0;
	const swayValue = (2 + (seed % 6)).toFixed(2);

	return {
		variant,
		attributes,
		accessories,
		mirror,
		swayValue,
	};
}
